import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Save,
  Eye,
  Upload,
  GripVertical,
  Trash2,
  RotateCcw,
  History,
  Palette,
  Image,
  Type,
  Layout,
  Check,
  X,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Sortable Section Item Component
const SortableSection = ({ section, onToggleVisibility, onEditSection, isActive }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
        isActive 
          ? "border-emerald-500 bg-emerald-500/10" 
          : section.visible 
            ? "border-white/10 bg-white/5 hover:border-white/20" 
            : "border-white/5 bg-white/[0.02] opacity-50"
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-stone-500 hover:text-stone-300"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      
      <div className="flex-1">
        <span className={`font-medium ${section.visible ? "text-white" : "text-stone-500"}`}>
          {section.name}
        </span>
      </div>
      
      <Switch
        checked={section.visible}
        onCheckedChange={() => onToggleVisibility(section.id)}
        className="data-[state=checked]:bg-emerald-500"
      />
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onEditSection(section)}
        className="text-stone-400 hover:text-white"
      >
        <Type className="w-4 h-4" />
      </Button>
    </div>
  );
};

// Section Editor Dialog - Enhanced with direct text editing and image management
const SectionEditorDialog = ({ section, isOpen, onClose, onSave, onImageUpload }) => {
  const [editedContent, setEditedContent] = useState(section?.content || {});
  const [customTexts, setCustomTexts] = useState(section?.customTexts || {});
  const [images, setImages] = useState(section?.images || []);
  const [activeTab, setActiveTab] = useState("texts");
  const [uploading, setUploading] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (section) {
      setEditedContent(section.content || {});
      setCustomTexts(section.customTexts || {});
      setImages(section.images || []);
    }
  }, [section]);

  const handleSave = () => {
    onSave(section.id, { 
      ...editedContent, 
      customTexts,
      images 
    });
    onClose();
  };

  const handleImageUpload = async (file) => {
    if (file) {
      setUploading(true);
      try {
        const imageUrl = await onImageUpload(file);
        if (imageUrl) {
          setImages(prev => [...prev, { id: Date.now(), url: imageUrl, alt: file.name }]);
          toast.success("Imagen agregada");
        }
      } catch (error) {
        toast.error("Error al subir imagen");
      } finally {
        setUploading(false);
      }
    }
  };

  const handleImageReplace = async (imageId, file) => {
    if (file) {
      setUploading(true);
      try {
        const imageUrl = await onImageUpload(file);
        if (imageUrl) {
          setImages(prev => prev.map(img => 
            img.id === imageId ? { ...img, url: imageUrl } : img
          ));
          toast.success("Imagen reemplazada");
        }
      } catch (error) {
        toast.error("Error al reemplazar imagen");
      } finally {
        setUploading(false);
      }
    }
  };

  const handleImageDelete = (imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
    toast.success("Imagen eliminada");
  };

  const handleContentImageChange = async (key, file) => {
    if (file) {
      setUploading(true);
      try {
        const imageUrl = await onImageUpload(file);
        if (imageUrl) {
          setEditedContent(prev => ({ ...prev, [key]: imageUrl }));
          toast.success("Imagen actualizada");
        }
      } catch (error) {
        toast.error("Error al subir imagen");
      } finally {
        setUploading(false);
      }
    }
  };

  const handleDeleteContentImage = (key) => {
    setEditedContent(prev => {
      const newContent = { ...prev };
      delete newContent[key];
      return newContent;
    });
    toast.success("Imagen eliminada");
  };

  // Add new custom text field
  const handleAddCustomText = () => {
    const newKey = `custom_text_${Date.now()}`;
    setCustomTexts(prev => ({ ...prev, [newKey]: { label: "Nuevo texto", value: "" } }));
  };

  const handleDeleteCustomText = (key) => {
    setCustomTexts(prev => {
      const newTexts = { ...prev };
      delete newTexts[key];
      return newTexts;
    });
  };

  // Add new image to content
  const handleAddContentImage = () => {
    const newKey = `image_${Date.now()}`;
    setEditedContent(prev => ({ ...prev, [newKey]: "" }));
  };

  if (!section) return null;

  // Get section-specific fields based on section type
  const getSectionFields = () => {
    const fields = {
      hero: [
        { key: "badge", label: "Badge/Etiqueta", type: "text" },
        { key: "title", label: "Título Principal", type: "text" },
        { key: "highlight", label: "Texto Destacado", type: "text" },
        { key: "description", label: "Descripción", type: "textarea" },
        { key: "cta_primary", label: "Botón Principal", type: "text" },
        { key: "cta_secondary", label: "Botón Secundario", type: "text" },
      ],
      features: [
        { key: "badge", label: "Badge/Etiqueta", type: "text" },
        { key: "title", label: "Título", type: "text" },
        { key: "highlight", label: "Texto Destacado", type: "text" },
        { key: "description", label: "Descripción", type: "textarea" },
      ],
      use_cases: [
        { key: "badge", label: "Badge/Etiqueta", type: "text" },
        { key: "title", label: "Título", type: "text" },
        { key: "highlight", label: "Texto Destacado", type: "text" },
      ],
      services: [
        { key: "badge", label: "Badge/Etiqueta", type: "text" },
        { key: "title", label: "Título", type: "text" },
        { key: "highlight", label: "Texto Destacado", type: "text" },
        { key: "description", label: "Descripción", type: "textarea" },
        { key: "print_image", label: "Imagen Impresión", type: "image" },
        { key: "digital_image", label: "Imagen Digital", type: "image" },
      ],
      allinone: [
        { key: "badge", label: "Badge/Etiqueta", type: "text" },
        { key: "title", label: "Título", type: "text" },
        { key: "highlight", label: "Texto Destacado", type: "text" },
        { key: "description", label: "Descripción", type: "textarea" },
      ],
      comparison: [
        { key: "badge", label: "Badge/Etiqueta", type: "text" },
        { key: "title", label: "Título", type: "text" },
        { key: "highlight", label: "Texto Destacado", type: "text" },
        { key: "description", label: "Descripción", type: "textarea" },
      ],
      pricing: [
        { key: "badge", label: "Badge/Etiqueta", type: "text" },
        { key: "title", label: "Título", type: "text" },
        { key: "highlight", label: "Texto Destacado", type: "text" },
        { key: "description", label: "Descripción", type: "textarea" },
        { key: "plan1_name", label: "Plan 1 - Nombre", type: "text", default: "Plan 250" },
        { key: "plan1_price", label: "Plan 1 - Precio", type: "text", default: "$165" },
        { key: "plan1_desc", label: "Plan 1 - Descripción", type: "text" },
        { key: "plan2_name", label: "Plan 2 - Nombre", type: "text", default: "Plan 500" },
        { key: "plan2_price", label: "Plan 2 - Precio", type: "text", default: "$325" },
        { key: "plan2_desc", label: "Plan 2 - Descripción", type: "text" },
        { key: "plan3_name", label: "Plan 3 - Nombre", type: "text", default: "Plan 1000" },
        { key: "plan3_price", label: "Plan 3 - Precio", type: "text", default: "$650" },
        { key: "plan3_desc", label: "Plan 3 - Descripción", type: "text" },
      ],
      cta: [
        { key: "title", label: "Título", type: "text" },
        { key: "description", label: "Descripción", type: "textarea" },
      ],
      contact: [
        { key: "title", label: "Título del Formulario", type: "text" },
      ],
    };
    return fields[section.id] || [];
  };

  const renderTextField = (field) => {
    const value = editedContent[field.key] || "";
    const isTranslationKey = typeof value === "string" && value.startsWith("landing_");
    const displayValue = isTranslationKey ? t(value) : value;
    
    // Check if there's a custom override
    const customValue = customTexts[field.key]?.value;
    const finalValue = customValue !== undefined ? customValue : displayValue;

    return (
      <div key={field.key} className="space-y-2">
        <Label className="text-stone-300 flex items-center gap-2">
          {field.label}
          {isTranslationKey && !customValue && (
            <span className="text-xs bg-stone-700 px-2 py-0.5 rounded">Auto-traducido</span>
          )}
        </Label>
        
        {field.type === "textarea" ? (
          <textarea
            value={customValue !== undefined ? customValue : ""}
            onChange={(e) => setCustomTexts(prev => ({ 
              ...prev, 
              [field.key]: { label: field.label, value: e.target.value }
            }))}
            placeholder={displayValue}
            rows={3}
            className="w-full bg-stone-800 border border-stone-700 text-white rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        ) : (
          <Input
            value={customValue !== undefined ? customValue : ""}
            onChange={(e) => setCustomTexts(prev => ({ 
              ...prev, 
              [field.key]: { label: field.label, value: e.target.value }
            }))}
            placeholder={displayValue}
            className="bg-stone-800 border-stone-700 text-white"
          />
        )}
        
        {isTranslationKey && (
          <p className="text-xs text-stone-500">
            Valor por defecto: {displayValue}
          </p>
        )}
        
        {customValue && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setCustomTexts(prev => {
                const newTexts = { ...prev };
                delete newTexts[field.key];
                return newTexts;
              });
            }}
            className="text-xs text-amber-400 hover:text-amber-300 p-0 h-auto"
          >
            Restaurar valor por defecto
          </Button>
        )}
      </div>
    );
  };

  const renderImageField = (field) => {
    const value = editedContent[field.key] || "";
    
    return (
      <div key={field.key} className="space-y-2">
        <Label className="text-stone-300">{field.label}</Label>
        
        {value ? (
          <div className="relative group">
            <img 
              src={value} 
              alt={field.label} 
              className="w-full h-40 object-cover rounded-lg border border-stone-700"
            />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleContentImageChange(field.key, e.target.files[0])}
                />
                <Button type="button" size="sm" variant="outline" className="border-white/50 text-white hover:bg-white/20">
                  <Upload className="w-4 h-4 mr-1" />
                  Reemplazar
                </Button>
              </label>
              <Button 
                type="button" 
                size="sm" 
                variant="outline" 
                className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                onClick={() => handleDeleteContentImage(field.key)}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Eliminar
              </Button>
            </div>
          </div>
        ) : (
          <label className="cursor-pointer block">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleContentImageChange(field.key, e.target.files[0])}
            />
            <div className="border-2 border-dashed border-stone-700 rounded-lg p-8 text-center hover:border-emerald-500 transition-colors">
              <Upload className="w-8 h-8 mx-auto mb-2 text-stone-500" />
              <p className="text-stone-400 text-sm">Click para subir imagen</p>
            </div>
          </label>
        )}
        
        <Input
          value={value}
          onChange={(e) => setEditedContent(prev => ({ ...prev, [field.key]: e.target.value }))}
          placeholder="O ingresa URL de imagen"
          className="bg-stone-800 border-stone-700 text-white text-sm"
        />
      </div>
    );
  };

  const sectionFields = getSectionFields();
  const textFields = sectionFields.filter(f => f.type === "text" || f.type === "textarea");
  const imageFields = sectionFields.filter(f => f.type === "image");

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-stone-900 border-stone-800 text-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Type className="w-5 h-5 text-emerald-400" />
            Editar: {section.name}
          </DialogTitle>
          <DialogDescription className="text-stone-400">
            Personaliza el contenido de esta sección
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="bg-stone-800 border-stone-700 w-full justify-start">
            <TabsTrigger value="texts" className="data-[state=active]:bg-emerald-600">
              <Type className="w-4 h-4 mr-2" />
              Textos ({textFields.length})
            </TabsTrigger>
            <TabsTrigger value="images" className="data-[state=active]:bg-emerald-600">
              <Image className="w-4 h-4 mr-2" />
              Imágenes ({imageFields.length})
            </TabsTrigger>
          </TabsList>
          
          <div className="flex-1 overflow-y-auto py-4">
            <TabsContent value="texts" className="mt-0 space-y-4">
              {textFields.length > 0 ? (
                textFields.map(field => renderTextField(field))
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <Type className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Esta sección no tiene campos de texto editables.</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="images" className="mt-0 space-y-4">
              {imageFields.length > 0 ? (
                imageFields.map(field => renderImageField(field))
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Esta sección no tiene campos de imagen predefinidos.</p>
                </div>
              )}
              
              {/* Additional Images Section */}
              <div className="border-t border-stone-700 pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-stone-300">Imágenes Adicionales</Label>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e.target.files[0])}
                    />
                    <Button type="button" size="sm" variant="outline" className="border-stone-700" disabled={uploading}>
                      <Upload className="w-4 h-4 mr-1" />
                      {uploading ? "Subiendo..." : "Agregar Imagen"}
                    </Button>
                  </label>
                </div>
                
                {images.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {images.map(img => (
                      <div key={img.id} className="relative group">
                        <img 
                          src={img.url} 
                          alt={img.alt || "Imagen"} 
                          className="w-full h-32 object-cover rounded-lg border border-stone-700"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleImageReplace(img.id, e.target.files[0])}
                            />
                            <Button type="button" size="sm" variant="outline" className="border-white/50 text-white hover:bg-white/20">
                              <Upload className="w-3 h-3" />
                            </Button>
                          </label>
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="outline" 
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                            onClick={() => handleImageDelete(img.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-stone-500 text-sm text-center py-4">
                    No hay imágenes adicionales. Haz click en "Agregar Imagen" para añadir.
                  </p>
                )}
              </div>
            </TabsContent>
          </div>
        </Tabs>
        
        <DialogFooter className="border-t border-stone-800 pt-4">
          <Button variant="outline" onClick={onClose} className="border-stone-700">
            Cancelar
          </Button>
          <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700">
            <Check className="w-4 h-4 mr-2" />
            Guardar Cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// History Dialog
const HistoryDialog = ({ isOpen, onClose, history, onRestore }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-stone-900 border-stone-800 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Historial de Versiones
          </DialogTitle>
          <DialogDescription className="text-stone-400">
            Últimas 5 versiones publicadas
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-2 py-4">
          {history.length === 0 ? (
            <p className="text-stone-500 text-center py-8">
              No hay versiones anteriores disponibles.
            </p>
          ) : (
            history.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 rounded-lg border border-white/10 bg-white/5"
              >
                <div>
                  <span className="font-medium text-white">Versión {item.version}</span>
                  <p className="text-xs text-stone-400">
                    {new Date(item.archived_at).toLocaleString()}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRestore(item.version)}
                  className="border-stone-700 text-stone-300 hover:text-white"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Restaurar
                </Button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Landing Editor Page
const LandingEditorPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [config, setConfig] = useState(null);
  const [publishedConfig, setPublishedConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const [configRes, publishedRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/admin/landing-config`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/admin/landing-config/published`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/admin/landing-config/history`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setConfig(configRes.data);
      setPublishedConfig(publishedRes.data);
      setHistory(historyRes.data);
      setHasChanges(configRes.data.status === "draft");
    } catch (error) {
      console.error("Error fetching config:", error);
      toast.error("Error al cargar la configuración");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setConfig(prev => {
        const oldIndex = prev.sections.findIndex(s => s.id === active.id);
        const newIndex = prev.sections.findIndex(s => s.id === over.id);
        
        const newSections = arrayMove(prev.sections, oldIndex, newIndex).map((s, idx) => ({
          ...s,
          order: idx
        }));
        
        return { ...prev, sections: newSections };
      });
      setHasChanges(true);
    }
  };

  const handleToggleVisibility = (sectionId) => {
    setConfig(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId ? { ...s, visible: !s.visible } : s
      )
    }));
    setHasChanges(true);
  };

  const handleEditSection = (section) => {
    setEditingSection(section);
    setActiveSection(section.id);
  };

  const handleSaveSection = (sectionId, content) => {
    setConfig(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId ? { ...s, content } : s
      )
    }));
    setHasChanges(true);
    toast.success("Sección actualizada");
  };

  const handleGlobalStyleChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      global_styles: { ...prev.global_styles, [key]: value }
    }));
    setHasChanges(true);
  };

  const handleImageUpload = async (file) => {
    try {
      const token = localStorage.getItem("token");
      const reader = new FileReader();
      
      return new Promise((resolve) => {
        reader.onload = async (e) => {
          const base64 = e.target.result;
          const response = await axios.post(
            `${API}/api/upload`,
            { image: base64 },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          resolve(response.data.url);
        };
        reader.readAsDataURL(file);
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error al subir imagen");
      return null;
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/admin/landing-config/save-draft`,
        config,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Borrador guardado");
      setHasChanges(true);
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Error al guardar borrador");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/admin/landing-config/publish`,
        config,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Versión ${response.data.version} publicada`);
      setHasChanges(false);
      fetchConfig();
    } catch (error) {
      console.error("Error publishing:", error);
      toast.error("Error al publicar");
    } finally {
      setPublishing(false);
    }
  };

  const handleDiscardDraft = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/admin/landing-config/draft`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Borrador descartado");
      fetchConfig();
    } catch (error) {
      console.error("Error discarding draft:", error);
      toast.error("Error al descartar borrador");
    }
  };

  const handleRestore = async (version) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/admin/landing-config/restore/${version}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Versión ${version} restaurada como borrador`);
      setShowHistory(false);
      fetchConfig();
    } catch (error) {
      console.error("Error restoring:", error);
      toast.error("Error al restaurar versión");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 text-white" data-testid="landing-editor-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-stone-900/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/admin")}
              className="text-stone-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">Editor de Landing Page</h1>
              <p className="text-xs text-stone-400">
                {config?.status === "draft" ? (
                  <span className="text-amber-400">Borrador sin publicar</span>
                ) : (
                  <span className="text-emerald-400">Versión {config?.version} publicada</span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHistory(true)}
              className="border-stone-700 text-stone-300"
            >
              <History className="w-4 h-4 mr-1" />
              Historial
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open("/", "_blank")}
              className="border-stone-700 text-stone-300"
            >
              <Eye className="w-4 h-4 mr-1" />
              Preview
            </Button>
            
            {hasChanges && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDiscardDraft}
                className="border-red-700 text-red-400 hover:bg-red-500/10"
              >
                <X className="w-4 h-4 mr-1" />
                Descartar
              </Button>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveDraft}
              disabled={saving}
              className="border-stone-700 text-stone-300"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? "Guardando..." : "Guardar Borrador"}
            </Button>
            
            <Button
              size="sm"
              onClick={handlePublish}
              disabled={publishing}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Check className="w-4 h-4 mr-1" />
              {publishing ? "Publicando..." : "Publicar"}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Sections */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-stone-900 border-stone-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Layout className="w-5 h-5 text-emerald-400" />
                  Secciones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-stone-400 mb-4">
                  Arrastra para reordenar. Usa el switch para mostrar/ocultar secciones.
                </p>
                
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={config?.sections?.map(s => s.id) || []}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {config?.sections?.sort((a, b) => a.order - b.order).map(section => (
                        <SortableSection
                          key={section.id}
                          section={section}
                          onToggleVisibility={handleToggleVisibility}
                          onEditSection={handleEditSection}
                          isActive={activeSection === section.id}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Global Styles */}
          <div className="space-y-6">
            <Card className="bg-stone-900 border-stone-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Palette className="w-5 h-5 text-emerald-400" />
                  Estilos Globales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-stone-300">Color Primario</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config?.global_styles?.primary_color || "#10b981"}
                      onChange={(e) => handleGlobalStyleChange("primary_color", e.target.value)}
                      className="w-12 h-10 p-1 bg-stone-800 border-stone-700 rounded cursor-pointer"
                    />
                    <Input
                      value={config?.global_styles?.primary_color || "#10b981"}
                      onChange={(e) => handleGlobalStyleChange("primary_color", e.target.value)}
                      className="bg-stone-800 border-stone-700 text-white flex-1"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-stone-300">Color Secundario</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config?.global_styles?.secondary_color || "#f59e0b"}
                      onChange={(e) => handleGlobalStyleChange("secondary_color", e.target.value)}
                      className="w-12 h-10 p-1 bg-stone-800 border-stone-700 rounded cursor-pointer"
                    />
                    <Input
                      value={config?.global_styles?.secondary_color || "#f59e0b"}
                      onChange={(e) => handleGlobalStyleChange("secondary_color", e.target.value)}
                      className="bg-stone-800 border-stone-700 text-white flex-1"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-stone-300">Color Acento</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config?.global_styles?.accent_color || "#ef4444"}
                      onChange={(e) => handleGlobalStyleChange("accent_color", e.target.value)}
                      className="w-12 h-10 p-1 bg-stone-800 border-stone-700 rounded cursor-pointer"
                    />
                    <Input
                      value={config?.global_styles?.accent_color || "#ef4444"}
                      onChange={(e) => handleGlobalStyleChange("accent_color", e.target.value)}
                      className="bg-stone-800 border-stone-700 text-white flex-1"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-stone-300">Color de Fondo</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config?.global_styles?.background_color || "#0c0a09"}
                      onChange={(e) => handleGlobalStyleChange("background_color", e.target.value)}
                      className="w-12 h-10 p-1 bg-stone-800 border-stone-700 rounded cursor-pointer"
                    />
                    <Input
                      value={config?.global_styles?.background_color || "#0c0a09"}
                      onChange={(e) => handleGlobalStyleChange("background_color", e.target.value)}
                      className="bg-stone-800 border-stone-700 text-white flex-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="bg-stone-900 border-stone-800">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-stone-400">
                    <p className="font-medium text-white mb-1">Tip</p>
                    <p>
                      Los cambios se guardan como borrador hasta que presiones "Publicar". 
                      Puedes restaurar versiones anteriores desde el historial.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Section Editor Dialog */}
      <SectionEditorDialog
        section={editingSection}
        isOpen={!!editingSection}
        onClose={() => {
          setEditingSection(null);
          setActiveSection(null);
        }}
        onSave={handleSaveSection}
        onImageUpload={handleImageUpload}
      />

      {/* History Dialog */}
      <HistoryDialog
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        history={history}
        onRestore={handleRestore}
      />
    </div>
  );
};

export default LandingEditorPage;
